import { Box, Checkbox, Chip, CircularProgress, Typography } from "@mui/material";
import { Colors } from "../../Colors";
import SettingsLayout from "./SettingsLayout";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { useEffect, useState } from "react";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { SuperOrganization } from "../../types";
import { setSuperOrganization, updateOrganizationAttribute } from "../../slices/AuthSlice";
import { api } from "../../utils/utils";
import { useSnackbar } from "../../providers/SnackbarProvider";
import SettingRow from "./SettingRow";

export default function Miscellaneous() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();

  const user = useSelector((state: RootState) => state.auth.user);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);
  const organization = useSelector((state: RootState) => state.auth.organization);

  const [ignorePreferredName, setIgnorePreferredName] = useState(superOrganization?.ignorePreferredName || false);
  const [ignoreFccHours, setIgnoreFccHours] = useState(organization?.ignoreFccHours || false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (superOrganization) {
      setIgnorePreferredName(superOrganization.ignorePreferredName || false);
    }
  }, [superOrganization]);

  useEffect(() => {
    if (organization) {
      setIgnoreFccHours(organization.ignoreFccHours || false);
    }
  }, [organization]);

  const handleSave = async () => {
    if (!user?.token || !superOrganization) {
      showMessage("User session or organization not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.patch(`/users/super-organization/${superOrganization.superOrganizationId}`, user.token, {
        ignorePreferredName,
      });
      dispatch(setSuperOrganization(response.data.superOrganization as SuperOrganization));
      await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "ignoreFccHours", value: ignoreFccHours })).unwrap();
      showMessage("Miscellaneous settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update miscellaneous settings:", error);
      showMessage("Failed to update miscellaneous settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsLayout>
      <Box pb={6}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Miscellaneous
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <SettingRow
            label="Ignore patient nickname field in WebPT"
            help={
              <Chip
                label="Company-Wide"
                size="small"
                sx={{
                  backgroundColor: `${Colors.info}20`,
                  color: Colors.info,
                  border: `1px solid ${Colors.info}40`,
                  fontSize: "0.7rem",
                  height: "20px",
                }}
              />
            }
          >
            <Checkbox
              checked={ignorePreferredName}
              onChange={(e) => setIgnorePreferredName(e.target.checked)}
            />
          </SettingRow>
          <SettingRow
            label="Ignore FCC restrictions"
            description="Always allow texts to be sent at all times. Only turn on for demo purposes."
          >
            <Checkbox
              checked={ignoreFccHours}
              onChange={(e) => setIgnoreFccHours(e.target.checked)}
            />
          </SettingRow>
        </Box>
        <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PrimaryButton onClick={handleSave} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
          </PrimaryButton>
        </Box>
      </Box>
    </SettingsLayout>
  );
}
